import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import './GroupAttendanceForm.css';

const GroupAttendanceForm = ({ onCancel, onSuccess }) => {
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().slice(0, 16));
  const [studentAttendances, setStudentAttendances] = useState({});

  // Obtener grupos
  const { data: groups } = useQuery('groups', async () => {
    const response = await api.get('/groups', { params: { is_active: true } });
    return response.data;
  });

  // Obtener matrículas del grupo seleccionado
  const { data: enrollments } = useQuery(
    ['enrollments', selectedGroupId],
    async () => {
      if (!selectedGroupId) return [];
      const response = await api.get('/enrollments', {
        params: { group_id: selectedGroupId, status: 'active' }
      });
      return response.data;
    },
    { enabled: !!selectedGroupId }
  );

  // Obtener estudiantes de las matrículas
  const { data: students } = useQuery('students', async () => {
    const response = await api.get('/students');
    return response.data;
  });

  // Obtener asistencias del grupo para la fecha seleccionada
  const selectedDate = attendanceDate ? attendanceDate.split('T')[0] : null;
  const { data: existingAttendances } = useQuery(
    ['attendances', selectedGroupId, selectedDate],
    async () => {
      if (!selectedGroupId || !selectedDate) return [];
      const response = await api.get('/attendances', {
        params: { 
          group_id: selectedGroupId,
          date_from: selectedDate,
          date_to: selectedDate
        }
      });
      return response.data;
    },
    { enabled: !!selectedGroupId && !!selectedDate }
  );

  // Inicializar estados de asistencia cuando cambian las matrículas o asistencias existentes
  useEffect(() => {
    if (enrollments && existingAttendances) {
      const initialAttendances = {};
      enrollments.forEach(enrollment => {
        const existing = existingAttendances.find(
          att => att.student_id === enrollment.student_id && 
                 att.enrollment_id === enrollment.id
        );
        if (existing) {
          initialAttendances[enrollment.student_id] = {
            status: existing.status,
            enrollment_id: enrollment.id,
            notes: existing.notes || ''
          };
        } else {
          initialAttendances[enrollment.student_id] = {
            status: 'present',
            enrollment_id: enrollment.id,
            notes: ''
          };
        }
      });
      setStudentAttendances(initialAttendances);
    } else if (enrollments) {
      const initialAttendances = {};
      enrollments.forEach(enrollment => {
        initialAttendances[enrollment.student_id] = {
          status: 'present',
          enrollment_id: enrollment.id,
          notes: ''
        };
      });
      setStudentAttendances(initialAttendances);
    }
  }, [enrollments, existingAttendances]);

  const createBulkAttendancesMutation = useMutation(
    (data) => api.post('/attendances/bulk', data),
    {
      onSuccess: () => {
        toast.success('Asistencias marcadas exitosamente!');
        onSuccess();
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail;
        toast.error(errorDetail || 'Error al marcar asistencias');
      },
    }
  );

  const handleStatusChange = (studentId, status) => {
    setStudentAttendances(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        status
      }
    }));
  };

  const handleNotesChange = (studentId, notes) => {
    setStudentAttendances(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        notes
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedGroupId) {
      toast.error('Selecciona un grupo');
      return;
    }

    if (!attendanceDate) {
      toast.error('Selecciona una fecha');
      return;
    }

    const selectedGroup = groups?.find(g => g.id === parseInt(selectedGroupId));
    if (!selectedGroup) {
      toast.error('Grupo no encontrado');
      return;
    }

    const attendancesToCreate = Object.entries(studentAttendances).map(([studentId, data]) => {
      const dateTime = new Date(attendanceDate);
      return {
        student_id: parseInt(studentId),
        enrollment_id: data.enrollment_id,
        group_id: parseInt(selectedGroupId),
        attendance_date: dateTime.toISOString(),
        status: data.status,
        mark_method: 'manual',
        notes: data.notes || null
      };
    });

    createBulkAttendancesMutation.mutate(attendancesToCreate);
  };

  const selectedGroup = groups?.find(g => g.id === parseInt(selectedGroupId));
  const enrollmentsList = enrollments || [];

  return (
    <form onSubmit={handleSubmit} className="group-attendance-form">
      <div className="form-group">
        <label htmlFor="group_id">Grupo *</label>
        <select
          id="group_id"
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          required
        >
          <option value="">Seleccionar grupo...</option>
          {groups?.map((group) => (
            <option key={group.id} value={group.id}>
              {group.discipline?.icon || '⚽'} {group.name} - {group.level?.name || 'Nivel'}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="attendance_date">Fecha y Hora *</label>
        <input
          type="datetime-local"
          id="attendance_date"
          value={attendanceDate}
          onChange={(e) => setAttendanceDate(e.target.value)}
          required
        />
      </div>

      {selectedGroup && enrollmentsList.length > 0 && (
        <div className="students-list">
          <h3>Estudiantes del Grupo ({enrollmentsList.length})</h3>
          <div className="students-grid">
            {enrollmentsList.map((enrollment) => {
              const student = students?.find(s => s.id === enrollment.student_id);
              const attendance = studentAttendances[enrollment.student_id];
              
              if (!student || !attendance) return null;

              return (
                <div key={enrollment.student_id} className="student-attendance-card">
                  <div className="student-name">
                    <strong>{student.first_name} {student.last_name}</strong>
                  </div>
                  <div className="attendance-controls">
                    <select
                      value={attendance.status}
                      onChange={(e) => handleStatusChange(enrollment.student_id, e.target.value)}
                      className={`status-select status-${attendance.status}`}
                    >
                      <option value="present">✅ Presente</option>
                      <option value="absent">❌ Ausente</option>
                      <option value="late">⏰ Tarde</option>
                      <option value="excused">📝 Justificado</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Notas (opcional)"
                      value={attendance.notes}
                      onChange={(e) => handleNotesChange(enrollment.student_id, e.target.value)}
                      className="notes-input"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedGroup && enrollmentsList.length === 0 && (
        <div className="empty-group-message">
          <p>Este grupo no tiene estudiantes matriculados activos.</p>
        </div>
      )}

      <div className="modal-actions">
        <button type="button" className="btn btn-cancel" onClick={onCancel} disabled={createBulkAttendancesMutation.isLoading}>
          Cancelar
        </button>
        <button 
          type="submit" 
          className="btn btn-primary" 
          disabled={!selectedGroupId || enrollmentsList.length === 0 || createBulkAttendancesMutation.isLoading}
        >
          {createBulkAttendancesMutation.isLoading ? 'Marcando...' : `Marcar ${enrollmentsList.length} Asistencia(s)`}
        </button>
      </div>
    </form>
  );
};

export default GroupAttendanceForm;

